/**
 * Regionalstatistik Import Page
 *
 * Two import modes:
 * 1. API Search - Search and download from Genesis API
 * 2. CSV Upload - Upload flat.csv files manually
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Upload,
  FileSpreadsheet,
  Database,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  X,
  Loader2,
  Download,
  Calendar,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  Building2,
  Home,
  Info,
  Settings,
  Key,
} from 'lucide-react'
import {
  useRSTableSearchAll,
  useRSTableMetadata,
  useRSUpload,
  useRSExecuteImport,
  useRSImportStatus,
  useRSImportedData,
  useRSCheckExisting,
  useGenesisInstances,
} from '../hooks/genesisQueries'
import { useEditions } from '../hooks/queries'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '../../lib/utils'
import type {
  RSTableSearchResult,
  RSUploadResponse,
  RSDataSourceInfo,
  GenesisInstance,
} from '../services/genesisApi'

type ImportMode = 'api' | 'upload'

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

// ============================================================================
// API Configuration Modal (shows both instances)
// ============================================================================

function ApiConfigModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { data: instances, refetch, isLoading } = useGenesisInstances()

  if (!isOpen) return null

  // Config info for each instance
  const instanceConfig: Record<GenesisInstance, {
    envLines: { key: string; placeholder: string }[]
    registerUrl: string
    registerLabel: string
  }> = {
    destatis: {
      envLines: [
        { key: 'GENESIS_TOKEN', placeholder: 'ihr_api_token' },
      ],
      registerUrl: 'https://www-genesis.destatis.de/genesis/online',
      registerLabel: 'destatis.de',
    },
    regionalstatistik: {
      envLines: [
        { key: 'REGIONALSTATISTIK_USER', placeholder: 'ihr_benutzername' },
        { key: 'REGIONALSTATISTIK_PASSWORD', placeholder: 'ihr_passwort' },
      ],
      registerUrl: 'https://www.regionalstatistik.de/genesis/online',
      registerLabel: 'regionalstatistik.de',
    },
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold">API-Konfiguration</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
          {/* Instance Status Cards */}
          {instances?.map((inst) => {
            const config = instanceConfig[inst.id]
            return (
              <div key={inst.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Instance Header */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 border-b">
                  <div
                    className={cn(
                      'w-3 h-3 rounded-full',
                      inst.connected
                        ? 'bg-green-500'
                        : inst.configured
                        ? 'bg-yellow-500'
                        : 'bg-gray-400'
                    )}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{inst.name}</p>
                    <p className="text-xs text-gray-500">
                      {inst.connected
                        ? `Verbunden als ${inst.username}`
                        : inst.configured
                        ? 'Konfiguriert, aber nicht verbunden'
                        : 'Nicht konfiguriert'}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400">
                    {inst.data_levels.join(', ')}
                  </div>
                </div>

                {/* Instance Config */}
                {!inst.connected && (
                  <div className="p-4 space-y-3">
                    <div className="bg-gray-900 rounded-lg p-3 font-mono text-xs">
                      {config.envLines.map((line, idx) => (
                        <div key={line.key} className={cn('text-gray-300', idx > 0 && 'mt-1')}>
                          <span className="text-blue-400">{line.key}</span>
                          <span className="text-gray-500">=</span>
                          <span className="text-green-400">{line.placeholder}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      Registrierung auf{' '}
                      <a
                        href={config.registerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        {config.registerLabel}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </p>
                  </div>
                )}
              </div>
            )
          })}

          {/* Restart Info */}
          <div className="flex items-start gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
            <div>
              <p>
                Nach Änderung der <code className="bg-blue-100 px-1 rounded">.env</code> Datei
                müssen die Container neu erstellt werden:
              </p>
              <code className="block mt-2 bg-blue-100 px-2 py-1 rounded text-blue-800">
                docker compose up -d backend celery-worker
              </code>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
            Status prufen
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Schliessen
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// API Status Badge (shows all instances)
// ============================================================================

function ApiStatusBadge() {
  const [showConfig, setShowConfig] = useState(false)
  const { data: instances, isLoading, refetch } = useGenesisInstances()

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Prufe API-Verbindungen...</span>
      </div>
    )
  }

  if (!instances) {
    return null
  }

  const allConnected = instances.every((i) => i.connected)
  const anyConnected = instances.some((i) => i.connected)

  return (
    <>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowConfig(true)}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
            allConnected
              ? 'bg-green-100 text-green-800 hover:bg-green-200'
              : anyConnected
              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
          title="API-Konfiguration offnen"
        >
          {/* Status dots for each instance */}
          <div className="flex gap-1">
            {instances.map((inst) => (
              <div
                key={inst.id}
                className={cn(
                  'w-2 h-2 rounded-full',
                  inst.connected ? 'bg-green-500' : inst.configured ? 'bg-yellow-500' : 'bg-gray-400'
                )}
                title={`${inst.name}: ${inst.connected ? 'verbunden' : inst.configured ? 'konfiguriert' : 'nicht konfiguriert'}`}
              />
            ))}
          </div>
          {allConnected
            ? 'APIs verbunden'
            : anyConnected
            ? 'Teilweise verbunden'
            : 'APIs nicht konfiguriert'}
          <Settings className="w-3.5 h-3.5 ml-1" />
        </button>
        <button
          onClick={() => refetch()}
          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          title="Status aktualisieren"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <ApiConfigModal isOpen={showConfig} onClose={() => setShowConfig(false)} />
    </>
  )
}

// ============================================================================
// Mode Toggle
// ============================================================================

function ModeToggle({
  mode,
  onChange,
}: {
  mode: ImportMode
  onChange: (mode: ImportMode) => void
}) {
  return (
    <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1">
      <button
        onClick={() => onChange('api')}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
          mode === 'api'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        )}
      >
        <Search className="w-4 h-4" />
        API-Suche
      </button>
      <button
        onClick={() => onChange('upload')}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
          mode === 'upload'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        )}
      >
        <Upload className="w-4 h-4" />
        CSV-Upload
      </button>
    </div>
  )
}

// Level labels for display
const LEVEL_LABELS: Record<string, string> = {
  bund: 'Bund',
  land: 'Land',
  kreis: 'Kreis',
  gemeinde: 'Gemeinde',
}

const LEVEL_COLORS: Record<string, string> = {
  bund: 'bg-purple-100 text-purple-800',
  land: 'bg-indigo-100 text-indigo-800',
  kreis: 'bg-blue-100 text-blue-800',
  gemeinde: 'bg-green-100 text-green-800',
}

const SOURCE_LABELS: Record<string, string> = {
  destatis: 'GENESIS',
  regionalstatistik: 'Regional',
}

const SOURCE_COLORS: Record<string, string> = {
  destatis: 'bg-amber-100 text-amber-800',
  regionalstatistik: 'bg-teal-100 text-teal-800',
}

// ============================================================================
// API Search Mode
// ============================================================================

function ApiSearchSection({
  onSelectTable,
  selectedTable,
}: {
  onSelectTable: (table: RSTableSearchResult | null) => void
  selectedTable: RSTableSearchResult | null
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [levelFilter, setLevelFilter] = useState<'all' | 'bund' | 'land' | 'kreis' | 'gemeinde'>('all')

  // Debounce search term (300ms) to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const searchParams =
    debouncedSearchTerm.length >= 2
      ? {
          term: debouncedSearchTerm,
          levelFilter: levelFilter !== 'all' ? levelFilter : undefined,
        }
      : null

  const { data: searchResponse, isFetching } = useRSTableSearchAll(searchParams)

  // Extract results and instance status from response
  const searchResults = searchResponse?.results ?? []
  const instanceStatus = searchResponse?.instance_status ?? []

  // Show loading when typing (before debounce) or when fetching
  const showLoading = (searchTerm.length >= 2 && searchTerm !== debouncedSearchTerm) || isFetching

  // Check for API issues
  const instanceIssues = instanceStatus.filter(
    (s) => !s.configured || !s.success
  )

  // Count results by source
  const resultCounts = searchResults.length > 0
    ? {
        destatis: searchResults.filter((r) => r.source === 'destatis').length,
        regionalstatistik: searchResults.filter((r) => r.source === 'regionalstatistik').length,
      }
    : { destatis: 0, regionalstatistik: 0 }

  return (
    <div className="space-y-4">
      {/* Search Input with Level Filter */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tabelle suchen (z.B. Bevölkerung, 12411, Arbeitslose, Brutto...)"
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value as typeof levelFilter)}
          className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">Alle Ebenen</option>
          <option value="bund">Nur Bund</option>
          <option value="land">Nur Länder</option>
          <option value="kreis">Nur Kreise</option>
          <option value="gemeinde">Nur Gemeinden</option>
        </select>
      </div>

      {/* Search Info */}
      {searchTerm.length > 0 && searchTerm.length < 2 && (
        <p className="text-sm text-gray-500">Mindestens 2 Zeichen eingeben...</p>
      )}

      {/* Loading */}
      {showLoading && (
        <div className="flex items-center gap-2 text-gray-500 py-4">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Suche in GENESIS und Regionalstatistik...</span>
        </div>
      )}

      {/* API Warnings */}
      {!showLoading && instanceIssues.length > 0 && debouncedSearchTerm.length >= 2 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
          {instanceIssues.map((issue) => (
            <div key={issue.instance} className="flex items-start gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium text-amber-800">
                  {issue.instance === 'destatis' ? 'GENESIS-Online (Destatis)' : 'Regionalstatistik'}:
                </span>
                <span className="text-amber-700 ml-1">
                  {!issue.configured
                    ? 'API nicht konfiguriert'
                    : issue.error || 'Suche fehlgeschlagen'}
                </span>
                {!issue.configured && (
                  <span className="text-amber-600 block mt-1 text-xs">
                    API-Zugangsdaten in den Einstellungen hinterlegen
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {searchResults && searchResults.length === 0 && !showLoading && debouncedSearchTerm.length >= 2 && (
        <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
          <Database className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600 font-medium">Keine Tabellen gefunden für "{debouncedSearchTerm}"</p>
          <p className="text-sm text-gray-500 mt-1">in beiden Datenquellen</p>
          <p className="text-xs text-gray-400 mt-3">
            Tipp: Versuchen Sie kürzere oder andere Suchbegriffe
          </p>
        </div>
      )}

      {/* Results */}
      {searchResults && searchResults.length > 0 && (
        <>
          {/* Result counts */}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">{searchResults.length} Ergebnisse:</span>
            {resultCounts.regionalstatistik > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 rounded">
                {resultCounts.regionalstatistik} Regional
              </span>
            )}
            {resultCounts.destatis > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded">
                {resultCounts.destatis} GENESIS
              </span>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-80 overflow-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Quelle
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Code
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Ebenen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {searchResults.map((table) => (
                    <tr
                      key={`${table.source}-${table.code}`}
                      onClick={() => onSelectTable(table)}
                      className={cn(
                        'cursor-pointer transition-colors',
                        selectedTable?.code === table.code && selectedTable?.source === table.source
                          ? 'bg-blue-50'
                          : 'hover:bg-gray-50'
                      )}
                    >
                      <td className="px-3 py-2">
                        <span className={cn('px-2 py-0.5 text-xs rounded', SOURCE_COLORS[table.source])}>
                          {SOURCE_LABELS[table.source]}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-sm text-gray-900">
                        {table.code}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 max-w-md truncate" title={table.name}>
                        {table.name}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {table.levels.map((level) => (
                            <span
                              key={level}
                              className={cn('px-1.5 py-0.5 text-xs rounded', LEVEL_COLORS[level])}
                            >
                              {LEVEL_LABELS[level]}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* External Links */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Info className="w-4 h-4" />
        <span>
          Tabellen durchsuchen auf{' '}
          <a
            href="https://www.regionalstatistik.de/genesis/online"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            regionalstatistik.de
            <ExternalLink className="w-3 h-3" />
          </a>
          {' '}oder{' '}
          <a
            href="https://www-genesis.destatis.de/genesis/online"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            genesis.destatis.de
            <ExternalLink className="w-3 h-3" />
          </a>
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// Table Metadata Panel
// ============================================================================

function TableMetadataPanel({
  table,
  selectedYear,
  onYearChange,
  onClear,
  instance = 'regionalstatistik',
}: {
  table: RSTableSearchResult
  selectedYear: number | null
  onYearChange: (year: number) => void
  onClear: () => void
  instance?: GenesisInstance
}) {
  const { data: metadata, isLoading } = useRSTableMetadata(table.code, instance)

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm bg-blue-100 px-2 py-0.5 rounded">
              {table.code}
            </span>
            <div className="flex gap-1">
              {table.is_gemeinde_level && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">
                  <Home className="w-3 h-3" />
                  Gemeinde
                </span>
              )}
              {table.is_kreis_level && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                  <Building2 className="w-3 h-3" />
                  Kreis
                </span>
              )}
            </div>
          </div>
          <h3 className="font-medium text-gray-900 mt-1">{table.name}</h3>
        </div>
        <button
          onClick={onClear}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Lade Metadaten...</span>
        </div>
      ) : metadata ? (
        <div className="space-y-3">
          {metadata.available_years.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Verfugbare Jahre
              </label>
              <div className="flex flex-wrap gap-2">
                {metadata.available_years.slice(-10).map((year) => (
                  <button
                    key={year}
                    onClick={() => onYearChange(year)}
                    className={cn(
                      'px-3 py-1.5 text-sm rounded-lg border transition-colors',
                      selectedYear === year
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                    )}
                  >
                    {year}
                  </button>
                ))}
              </div>
              {metadata.available_years.length > 10 && (
                <p className="text-xs text-gray-500 mt-1">
                  Weitere Jahre verfugbar: {metadata.time_from} - {metadata.time_to}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Keine Jahresinformationen verfugbar</p>
          )}
        </div>
      ) : null}
    </div>
  )
}

// ============================================================================
// CSV Upload Section
// ============================================================================

function CSVUploadSection({
  onUploadComplete,
  uploadResponse,
}: {
  onUploadComplete: (response: RSUploadResponse) => void
  uploadResponse: RSUploadResponse | null
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const uploadMutation = useRSUpload()

  const handleFile = useCallback(
    async (file: File) => {
      try {
        const response = await uploadMutation.mutateAsync(file)
        onUploadComplete(response)
      } catch (error) {
        console.error('Upload failed:', error)
      }
    },
    [uploadMutation, onUploadComplete]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  if (uploadResponse) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Datei hochgeladen</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-green-600">Datei</span>
            <p className="font-medium text-green-900">{uploadResponse.filename}</p>
          </div>
          <div>
            <span className="text-green-600">Tabelle</span>
            <p className="font-mono text-green-900">{uploadResponse.table_code}</p>
          </div>
          <div>
            <span className="text-green-600">{uploadResponse.time_label || 'Jahr'}</span>
            <p className="font-medium text-green-900">{uploadResponse.time_value || uploadResponse.detected_year}</p>
          </div>
          <div>
            <span className="text-green-600">Zeilen</span>
            <p className="font-medium text-green-900">{uploadResponse.row_count.toLocaleString('de-DE')}</p>
          </div>
        </div>

        {/* Entity Counts */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(uploadResponse.entity_counts).map(([level, count]) => (
            <span
              key={level}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded"
            >
              {level}: {count.toLocaleString('de-DE')}
            </span>
          ))}
        </div>

        {/* Indicators Preview */}
        {uploadResponse.indicators.length > 0 && (
          <div>
            <p className="text-sm text-green-700 mb-2">
              {uploadResponse.indicators.length} Indikatoren erkannt:
            </p>
            <div className="max-h-40 overflow-auto bg-white rounded border border-green-200">
              <table className="min-w-full text-sm">
                <thead className="bg-green-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-1.5 text-left text-xs text-green-700">Code</th>
                    <th className="px-3 py-1.5 text-left text-xs text-green-700">Name</th>
                    <th className="px-3 py-1.5 text-left text-xs text-green-700">Einheit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-100">
                  {uploadResponse.indicators.slice(0, 10).map((ind) => (
                    <tr key={ind.code}>
                      <td className="px-3 py-1.5 font-mono text-xs">{ind.code}</td>
                      <td className="px-3 py-1.5">{ind.name}</td>
                      <td className="px-3 py-1.5 text-gray-500">{ind.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {uploadResponse.indicators.length > 10 && (
                <p className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
                  ... und {uploadResponse.indicators.length - 10} weitere
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        )}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleFile(file)
          }}
        />

        {uploadMutation.isPending ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            <p className="text-gray-600">Datei wird analysiert...</p>
          </div>
        ) : (
          <>
            <FileSpreadsheet className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">
              Klicken oder Datei hierher ziehen
            </p>
            <p className="text-sm text-gray-500 mt-1">
              flat.csv Format (von regionalstatistik.de)
            </p>
          </>
        )}
      </div>

      {uploadMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span>Upload fehlgeschlagen: {uploadMutation.error.message}</span>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-gray-700">Flat-CSV Format</p>
          <p>
            Laden Sie die CSV-Datei im "flat"-Format herunter. Diese enthalt eine Zeile
            pro Wert/Dimension-Kombination.
          </p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Import Execution Section
// ============================================================================

function ImportExecutionSection({
  mode,
  selectedTable,
  selectedYear,
  uploadResponse,
  onReset,
}: {
  mode: ImportMode
  selectedTable: RSTableSearchResult | null
  selectedYear: number | null
  uploadResponse: RSUploadResponse | null
  onReset: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [targetEditionId, setTargetEditionId] = useState<number | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [forceUpdate, setForceUpdate] = useState(false)

  const { data: editionsData } = useEditions('GV100')
  const executeMutation = useRSExecuteImport()
  const { data: jobStatus } = useRSImportStatus(jobId, {
    refetchInterval: jobId ? 1000 : false,
  })

  // Determine table code and year for duplicate check
  const checkTableCode = mode === 'api' ? selectedTable?.code : uploadResponse?.table_code
  const checkYear = mode === 'api' ? selectedYear : uploadResponse?.detected_year

  // Check for existing data
  const { data: existingCheck } = useRSCheckExisting(
    checkTableCode || null,
    checkYear || null
  )

  // Invalidate queries when job completes
  useEffect(() => {
    if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
      // Refresh data lists
      queryClient.invalidateQueries({ queryKey: ['regionalstatistik', 'data'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    }
  }, [jobStatus?.status, queryClient])

  const canExecute =
    targetEditionId !== null &&
    ((mode === 'api' && selectedTable && selectedYear) ||
      (mode === 'upload' && uploadResponse))

  // If data exists and user hasn't confirmed update, show warning
  const needsConfirmation = existingCheck?.exists && !forceUpdate

  const handleExecute = async () => {
    if (!targetEditionId) return
    if (needsConfirmation) return // Should not happen, but safety check

    try {
      const request =
        mode === 'api' && selectedTable && selectedYear
          ? {
              table_code: selectedTable.code,
              year: selectedYear,
              target_edition_id: targetEditionId,
              force_update: forceUpdate,
            }
          : uploadResponse
          ? {
              upload_id: uploadResponse.upload_id,
              target_edition_id: targetEditionId,
              force_update: forceUpdate,
            }
          : null

      if (!request) return

      const response = await executeMutation.mutateAsync(request)
      if (response.job_id) {
        setJobId(response.job_id)
      }
    } catch (error) {
      console.error('Import failed:', error)
    }
  }

  // Job completed view
  if (jobStatus?.status === 'completed' && jobStatus.result) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800 mb-3">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Import abgeschlossen</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-green-600">Gesamt</span>
              <p className="font-medium text-green-900">
                {jobStatus.result.total_records.toLocaleString('de-DE')}
              </p>
            </div>
            <div>
              <span className="text-green-600">Erfolgreich</span>
              <p className="font-medium text-green-900">
                {jobStatus.result.successful_records.toLocaleString('de-DE')}
              </p>
            </div>
            <div>
              <span className="text-green-600">Fehlgeschlagen</span>
              <p className="font-medium text-red-600">
                {jobStatus.result.failed_records.toLocaleString('de-DE')}
              </p>
            </div>
            <div>
              <span className="text-green-600">Dauer</span>
              <p className="font-medium text-green-900">
                {jobStatus.result.duration_seconds.toFixed(1)}s
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Neuer Import
          </button>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            Zum Dashboard
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  // Job failed view
  if (jobStatus?.status === 'failed') {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 mb-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Import fehlgeschlagen</span>
          </div>
          <p className="text-sm text-red-700">{jobStatus.message}</p>
          {jobStatus.result?.errors && jobStatus.result.errors.length > 0 && (
            <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
              {jobStatus.result.errors.slice(0, 5).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          )}
        </div>

        <button
          onClick={onReset}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Erneut versuchen
        </button>
      </div>
    )
  }

  // Job running view
  if (jobId && jobStatus && (jobStatus.status === 'pending' || jobStatus.status === 'running')) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800 mb-3">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-medium">Import lauft...</span>
          </div>

          {jobStatus.progress !== null && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-blue-600">{jobStatus.message}</span>
                <span className="font-medium">{jobStatus.progress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${jobStatus.progress}%` }}
                />
              </div>
            </div>
          )}

          <p className="text-sm text-blue-600 mt-3">Job-ID: {jobId}</p>
        </div>
      </div>
    )
  }

  // Configuration view
  return (
    <div className="space-y-4">
      {/* Edition Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Database className="w-4 h-4 inline mr-1" />
          GV100-Edition (Gebietsstand)
        </label>
        <select
          value={targetEditionId || ''}
          onChange={(e) => setTargetEditionId(e.target.value ? Number(e.target.value) : null)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Edition wahlen...</option>
          {editionsData?.editions?.map((edition) => (
            <option key={edition.id} value={edition.id}>
              {edition.edition} (Referenzjahr {edition.reference_year})
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Die Daten werden der ausgewahlten GV100-Edition zugeordnet
        </p>
      </div>

      {/* Import Summary */}
      {canExecute && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <h4 className="font-medium text-gray-900">Import-Zusammenfassung</h4>
          <div className="text-sm text-gray-600 space-y-1">
            {mode === 'api' && selectedTable && (
              <>
                <p>
                  <span className="text-gray-500">Tabelle:</span>{' '}
                  <span className="font-mono">{selectedTable.code}</span> - {selectedTable.name}
                </p>
                <p>
                  <span className="text-gray-500">Jahr:</span> {selectedYear}
                </p>
              </>
            )}
            {mode === 'upload' && uploadResponse && (
              <>
                <p>
                  <span className="text-gray-500">Datei:</span> {uploadResponse.filename}
                </p>
                <p>
                  <span className="text-gray-500">Tabelle:</span>{' '}
                  <span className="font-mono">{uploadResponse.table_code}</span>
                  {uploadResponse.table_name && ` - ${uploadResponse.table_name}`}
                </p>
                <p>
                  <span className="text-gray-500">{uploadResponse.time_label || 'Jahr'}:</span> {uploadResponse.time_value || uploadResponse.detected_year}
                </p>
                <p>
                  <span className="text-gray-500">Zeilen:</span>{' '}
                  {uploadResponse.row_count.toLocaleString('de-DE')}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Existing Data Warning */}
      {existingCheck?.exists && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-amber-800 mb-1">
                Daten bereits vorhanden
              </h4>
              <p className="text-sm text-amber-700 mb-3">
                {existingCheck.message}
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={forceUpdate}
                  onChange={(e) => setForceUpdate(e.target.checked)}
                  className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-sm text-amber-800 font-medium">
                  Bestehende Daten aktualisieren (überschreiben)
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Execute Button */}
      <div className="flex gap-3">
        <button
          onClick={handleExecute}
          disabled={!canExecute || executeMutation.isPending || needsConfirmation}
          className={cn(
            'flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors',
            canExecute && !executeMutation.isPending && !needsConfirmation
              ? forceUpdate
                ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          )}
        >
          {executeMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Wird gestartet...
            </>
          ) : forceUpdate ? (
            <>
              <RefreshCw className="w-4 h-4" />
              Daten aktualisieren
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Import starten
            </>
          )}
        </button>
      </div>

      {/* Validation Warnings */}
      {!targetEditionId && (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <AlertTriangle className="w-4 h-4" />
          <span>Bitte wahlen Sie eine GV100-Edition aus</span>
        </div>
      )}

      {mode === 'api' && !selectedTable && (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <AlertTriangle className="w-4 h-4" />
          <span>Bitte wahlen Sie eine Tabelle aus</span>
        </div>
      )}

      {mode === 'api' && selectedTable && !selectedYear && (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <AlertTriangle className="w-4 h-4" />
          <span>Bitte wahlen Sie ein Jahr aus</span>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Import Dialog
// ============================================================================

function ImportDialog({
  isOpen,
  onClose,
  onImportComplete,
}: {
  isOpen: boolean
  onClose: () => void
  onImportComplete: () => void
}) {
  const [mode, setMode] = useState<ImportMode>('api')  // Default to API mode
  const [selectedTable, setSelectedTable] = useState<RSTableSearchResult | null>(null)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [uploadResponse, setUploadResponse] = useState<RSUploadResponse | null>(null)

  const handleReset = () => {
    setSelectedTable(null)
    setSelectedYear(null)
    setUploadResponse(null)
  }

  const handleModeChange = (newMode: ImportMode) => {
    setMode(newMode)
    handleReset()
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold">Neuer Import</h2>
            <p className="text-sm text-gray-500">Daten aus GENESIS/Regionalstatistik importieren</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Mode Toggle */}
          <ModeToggle mode={mode} onChange={handleModeChange} />

          {/* API Search Mode */}
          {mode === 'api' && (
            <>
              <ApiSearchSection
                onSelectTable={setSelectedTable}
                selectedTable={selectedTable}
              />

              {selectedTable && (
                <TableMetadataPanel
                  table={selectedTable}
                  selectedYear={selectedYear}
                  onYearChange={setSelectedYear}
                  onClear={() => {
                    setSelectedTable(null)
                    setSelectedYear(null)
                  }}
                  instance={selectedTable.source}
                />
              )}
            </>
          )}

          {/* CSV Upload Mode */}
          {mode === 'upload' && (
            <CSVUploadSection
              onUploadComplete={setUploadResponse}
              uploadResponse={uploadResponse}
            />
          )}

          {/* Import Configuration */}
          {((mode === 'api' && selectedTable && selectedYear) ||
            (mode === 'upload' && uploadResponse)) && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import konfigurieren</h3>
              <ImportExecutionSection
                mode={mode}
                selectedTable={selectedTable}
                selectedYear={selectedYear}
                uploadResponse={uploadResponse}
                onReset={() => {
                  handleReset()
                  onImportComplete()
                  onClose()
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Data Source Card (Business Card Style)
// ============================================================================

function DataSourceCard({ dataSource }: { dataSource: RSDataSourceInfo }) {
  // Entity level label mapping
  const entityLevelLabels: Record<string, string> = {
    'municipality': 'Gemeinde',
    'county': 'Kreis',
    'state': 'Land',
    'federal': 'Bund',
    'government_district': 'Reg.Bez.',
    'municipal_association': 'Gem.Verb.',
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Header: Table Code as main title */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {dataSource.statistics_label && (
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                {dataSource.statistics_label}
              </span>
            )}
            {dataSource.is_latest && (
              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                Aktuell
              </span>
            )}
          </div>
          <h3 className="font-mono font-medium text-gray-900">
            {dataSource.table_code}
          </h3>
        </div>
      </div>

      {/* Time/Year */}
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
        <Calendar className="w-3.5 h-3.5 text-gray-400" />
        <span>
          {dataSource.time_label ? `${dataSource.time_label}: ` : ''}
          {dataSource.time_value || dataSource.reference_year}
        </span>
      </div>

      {/* Indicators */}
      {dataSource.indicators && dataSource.indicators.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-400 mb-1">Indikatoren:</p>
          <div className="flex flex-wrap gap-1">
            {dataSource.indicators.slice(0, 3).map((ind, idx) => (
              <span
                key={idx}
                className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200"
                title={`${ind.name}${ind.unit ? ` (${ind.unit})` : ''}`}
              >
                {ind.name.length > 25 ? ind.name.slice(0, 25) + '...' : ind.name}
              </span>
            ))}
            {dataSource.indicators.length > 3 && (
              <span className="text-xs text-gray-400 px-1">
                +{dataSource.indicators.length - 3} weitere
              </span>
            )}
          </div>
        </div>
      )}

      {/* Entity Level Tags */}
      {dataSource.entity_levels && dataSource.entity_levels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {dataSource.entity_levels.map((level) => {
            const count = dataSource.entity_counts?.[level]
            return (
              <span
                key={level}
                className={cn(
                  'inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded',
                  level === 'municipality' ? 'bg-green-50 text-green-700 border border-green-200' :
                  level === 'county' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                  'bg-gray-50 text-gray-600 border border-gray-200'
                )}
              >
                {level === 'municipality' && <Home className="w-3 h-3" />}
                {level === 'county' && <Building2 className="w-3 h-3" />}
                {entityLevelLabels[level] || level}
                {count && <span className="text-[10px] opacity-70">({count.toLocaleString('de-DE')})</span>}
              </span>
            )
          })}
        </div>
      )}

      {/* Footer: Records + Import Date + Edition */}
      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
        <span>{dataSource.records_imported.toLocaleString('de-DE')} Datensatze</span>
        <div className="flex items-center gap-2">
          {dataSource.parent_edition_name && (
            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
              Edition {dataSource.parent_edition_name}
            </span>
          )}
          <span>{new Date(dataSource.import_date).toLocaleDateString('de-DE')}</span>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Imported Data Grid
// ============================================================================

function ImportedDataTable({ onRefresh }: { onRefresh: number }) {
  const { data: dataSources, isLoading, refetch } = useRSImportedData()
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (onRefresh > 0) {
      refetch()
    }
  }, [onRefresh, refetch])

  // Filter and sort data sources
  const filteredAndSorted = useMemo(() => {
    if (!dataSources) return []

    let result = [...dataSources]

    // Filter by search term (table code, statistics, or indicators)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter((ds) =>
        ds.table_code.toLowerCase().includes(term) ||
        (ds.statistics_label && ds.statistics_label.toLowerCase().includes(term)) ||
        (ds.statistics_code && ds.statistics_code.toLowerCase().includes(term)) ||
        (ds.indicators && ds.indicators.some(ind =>
          ind.name.toLowerCase().includes(term) ||
          (ind.code && ind.code.toLowerCase().includes(term))
        ))
      )
    }

    // Sort by table_code
    result.sort((a, b) => a.table_code.localeCompare(b.table_code))

    return result
  }, [dataSources, searchTerm])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!dataSources || dataSources.length === 0) {
    return (
      <div className="text-center py-12">
        <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500 mb-2">Keine Regionalstatistik-Daten importiert</p>
        <p className="text-sm text-gray-400">
          Klicken Sie auf "Neuer Import" um Daten zu importieren
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Tabelle oder Statistik suchen..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {searchTerm && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            {filteredAndSorted.length} von {dataSources.length}
          </span>
        )}
      </div>

      {/* Grid */}
      {filteredAndSorted.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Ergebnisse fur "{searchTerm}"
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAndSorted.map((ds) => (
            <DataSourceCard key={ds.id} dataSource={ds} />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function GenesisImport() {
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const handleImportComplete = () => {
    setRefreshKey((k) => k + 1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">GENESIS</h1>
          <p className="text-gray-500 mt-1">
            Daten aus GENESIS-Online und Regionalstatistik
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ApiStatusBadge />
          <button
            onClick={() => setShowImportDialog(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Neuer Import
          </button>
        </div>
      </div>

      {/* Imported Data */}
      <ImportedDataTable onRefresh={refreshKey} />

      {/* Import Dialog */}
      <ImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImportComplete={handleImportComplete}
      />
    </div>
  )
}
